import { useState, useTransition } from "react";
import { useTranslations } from "use-intl";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Stepper } from "@/components/app/stepper";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { deleteLoggedSet, updateSet } from "@/lib/actions";
import { ApiError } from "@/lib/api";

export type EditableSet = {
  id: string;
  weight: number | null;
  reps: number | null;
  durationSec: number | null;
  distanceKm: number | null;
};

/** Corrects or deletes a set of a finished workout. */
export function EditSetDrawer({
  set,
  title,
  type,
  weightStep,
  onClose,
}: {
  set: EditableSet | null;
  title: string;
  type: "reps" | "time" | "cardio";
  weightStep: number;
  onClose: () => void;
}) {
  return (
    <Drawer open={set != null} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent>
        {set && <EditSetForm key={set.id} set={set} title={title} type={type} weightStep={weightStep} onClose={onClose} />}
      </DrawerContent>
    </Drawer>
  );
}

function EditSetForm({
  set,
  title,
  type,
  weightStep,
  onClose,
}: {
  set: EditableSet;
  title: string;
  type: "reps" | "time" | "cardio";
  weightStep: number;
  onClose: () => void;
}) {
  const t = useTranslations("progress");
  const tt = useTranslations("train");
  const tc = useTranslations("common");
  const [d, setD] = useState(set);
  const [pending, start] = useTransition();
  const invalid = type === "reps" ? !d.reps : !d.durationSec;

  const run = (fn: () => Promise<void>) =>
    start(async () => {
      try {
        await fn();
        toast.success(tc("saved"));
        onClose();
      } catch (e) {
        toast.error(e instanceof ApiError && e.message === "LAST_SET" ? t("lastSet") : tc("error"));
      }
    });

  return (
    <>
      <DrawerHeader className="text-left">
        <DrawerTitle>
          {t("editSet")} · {title}
        </DrawerTitle>
        <DrawerDescription>{t("editSetHint")}</DrawerDescription>
      </DrawerHeader>
      <div className="space-y-4 px-4 pb-8">
        {type === "reps" && (
          <div className="grid grid-cols-2 gap-2">
            <Stepper
              label={`${tt("weight")} (kg)`}
              value={d.weight}
              step={weightStep || 2.5}
              decimals={2}
              max={1000}
              onChange={(weight) => setD({ ...d, weight })}
            />
            <Stepper label={tt("reps")} value={d.reps} max={500} onChange={(reps) => setD({ ...d, reps })} />
          </div>
        )}
        {type === "time" && (
          <Stepper
            label={tt("duration")}
            value={d.durationSec}
            step={5}
            max={36000}
            onChange={(durationSec) => setD({ ...d, durationSec })}
          />
        )}
        {type === "cardio" && (
          <div className="grid grid-cols-2 gap-2">
            <Stepper
              label={tt("minutes")}
              value={d.durationSec != null ? Math.round(d.durationSec / 60) : null}
              max={600}
              onChange={(m) => setD({ ...d, durationSec: m == null ? null : m * 60 })}
            />
            <Stepper
              label={tt("distance")}
              value={d.distanceKm}
              step={0.1}
              decimals={2}
              max={500}
              onChange={(distanceKm) => setD({ ...d, distanceKm })}
            />
          </div>
        )}
        <Button
          size="lg"
          className="h-12 w-full rounded-2xl text-base"
          disabled={pending || invalid}
          onClick={() =>
            run(() =>
              updateSet(set.id, { weight: d.weight, reps: d.reps, durationSec: d.durationSec, distanceKm: d.distanceKm }),
            )
          }
        >
          {pending && <Loader2 className="animate-spin" />}
          {tc("save")}
        </Button>
        <Button variant="ghost" className="w-full text-destructive" disabled={pending} onClick={() => run(() => deleteLoggedSet(set.id))}>
          <Trash2 /> {t("deleteSet")}
        </Button>
      </div>
    </>
  );
}
